import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import Logo from '../../assets/logo_jothi.jpg';
import { PageLayout, Table, Button, DatePicker, LogoLoader } from "../../components/ui";
import Loader from '../../components/ui/Loader';
// amountToRelease / gst / processingCharge store PAISE.
import { toRupees } from "../../utils/money";

const TODAY = () => new Date().toISOString().split("T")[0];

const fmt = (d) => {
  if (!d) return "—";
  const [y, m, dd] = String(d).split("T")[0].split("-");
  return `${dd}/${m}/${y}`;
};

// Rows that look like a cash/bank withdrawal but are NOT one.
//
// An EMI is a MEMBER SAVINGS movement. The money entered Cash once already, as
// a member deposit (Dr Cash / Cr Member Savings); the EMI then settles against
// that savings balance —
//     Dr Member Savings / Cr Loans Outstanding + Cr Interest Income
// — and never touches Cash. Listing it in a Cash ledger deducts the same money
// a second time: Palani Cash closed at Rs.9,834 against a drawer holding
// Rs.16,000, and across all three branches Rs.2,23,897 was understated.
//
// `cash_type='cash'` on one of these rows means "the member originally paid in
// cash", NOT "cash left the drawer". A `transactions` row carries no marker for
// which ledger account it belongs to, so the channel fetch below has to guess —
// and for this row shape the guess is wrong.
//
// The postedTxnIds dedup guard cannot catch them: an EMI posts to the ledger
// under sourceModel='loanstatement_applied', keyed by the LOANSTATEMENT id, so
// there is no back-link to the transactions row for the guard to match.
//
// Same three prefixes as isMemberWithdrawal() in ledgerPosting.js:2187, which
// already excludes them from ledger posting for this exact reason. The report
// disagreeing with the backend about what counts as a cash movement IS the bug,
// so the two lists must stay equal — change them together.
const SYSTEM_WITHDRAWAL_PREFIXES = [
  'EMI Payment for',
  'EMI Auto-Deduct',
  'Loan Foreclosure',
];

const isSystemWithdrawal = (t) => {
  // accountType alone is not enough: 8 of the 18 affected rows predate that
  // column being populated and carry NULL, so narration is the only field that
  // identifies every one.
  if (String(t?.accountType || "").trim() === "Loan Account") return true;
  const narration = String(t?.narration || "");
  return SYSTEM_WITHDRAWAL_PREFIXES.some((p) => narration.startsWith(p));
};

const LedgerReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [fromDate, setFromDate] = useState(TODAY());
  const [toDate, setToDate] = useState(TODAY());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loanInfo, setLoanInfo] = useState(null);
  const [loanStatements, setLoanStatements] = useState([]);

  // Search popup state
  const [showSearch, setShowSearch] = useState(false);
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [selectedLedger, setSelectedLedger] = useState(null); // { id, label, type, unique_id }

  // Data for search popup
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [members, setMembers] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => { loadSearchData(); }, []);

  // Reads ledger_account under base_ledger_group, NOT ledger_group under
  // account_group. Those two tables were built but never populated (0 rows), so
  // searching "Cash" returned nothing while five Cash accounts existed. Same wrong
  // pair Balancesheet.js read before it was corrected.
  const loadSearchData = async () => {
    try {
      const branchParam = (!isSuperAdmin && branchId) ? { branchId } : {};
      const [laRes, bgRes, mbRes, stRes, brRes] = await Promise.all([
        axios.get("/api/getModelData/ledger_account", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
        axios.get("/api/getModelData/base_ledger_group", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
        axios.get("/api/getModelData/personal_details", { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
        axios.get("/api/getModelData/staff_details", { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
        // Names the branch blocks in a group report ("Branch 1 · Rameswaram").
        axios.get("/api/getModelData/branch_details", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
      ]);
      const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];

      const groups = toArr(bgRes);
      const groupOf = (ledgerId) => groups.find(g => Number(g.id) === Number(ledgerId));

      // Branch-scoped for ordinary users; shared accounts (branchId NULL) belong to
      // no single branch and stay visible to everyone.
      const accounts = toArr(laRes)
        .filter(a => isSuperAdmin || !branchId || a.branchId == null || Number(a.branchId) === Number(branchId))
        .map(a => {
          const g = groupOf(a.ledger_id);
          return {
            id: a.id,
            name: a.name,
            branchId: a.branchId,
            ledgerId: a.ledger_id,
            groupName: g?.group_name || "",
            // Classified by GROUP, never by name: "Loans Outstanding - FEDERAL BANK"
            // contains "bank" while being an asset, not a bank account.
            kind: g?.group_name === "Cash In Hand" ? "cash"
              : g?.group_name === "Bank Accounts" ? "bank"
                : g?.group_name === "Loans Outstanding" ? "loans"
                  : null,
          };
        });

      // Account GROUPS are selectable in their own right. Without them the only
      // way to read a whole group is to run one report per account and add them
      // up: "Loans Outstanding" alone is 32 accounts, and its Rs26.46 crore total
      // was unobtainable from this screen at any price.
      //
      // Excluded:
      //   - legacy groups (SBI, SOFTWARE, BUS FARE, Dhanalakshmi, ' Cash'), which
      //     hold one account each and carry old group_type values (BankAccount,
      //     DirectIncome) that fall outside every Balance Sheet bucket. Their
      //     accounts stay individually searchable.
      //   - groups with no account visible to this user, which would report empty.
      //
      // Counted AFTER the branch filter above, so a branch admin sees "8 accounts"
      // rather than a promise of 32 rows they cannot see.
      const LEGACY_GROUP_TYPES = ["BankAccount", "DirectIncome", "DirectExpenses"];
      const groupList = groups
        .filter(g => !LEGACY_GROUP_TYPES.includes(String(g.group_type || "").trim()))
        .map(g => ({
          id: g.id,
          name: g.group_name,
          groupType: g.group_type || "",
          accountCount: accounts.filter(a => Number(a.ledgerId) === Number(g.id)).length,
        }))
        .filter(g => g.accountCount > 0);

      setLedgerGroups(accounts);
      setAccountGroups(groupList);
      setBranches(toArr(brRes));
      setMembers(toArr(mbRes));
      setStaff(toArr(stRes));
    } catch (err) {
      console.error("Error loading search data:", err);
    }
  };

  // Combined search entries.
  //
  // GROUPS FIRST, always. Searching "Loans Outstanding" matches 32 accounts in a
  // dialog that shows about 7 — in natural order the one row that covers them all
  // lands somewhere in the middle of a scroll.
  //
  // `sub` carries the group's own type and its account count, so the operator
  // knows the size of what they are about to pull before selecting it.
  const searchEntries = [
    ...accountGroups.map(g => ({
      id: g.id,
      label: g.name,
      sub: `${g.groupType} · ${g.accountCount} account${g.accountCount === 1 ? "" : "s"}`,
      type: "group",
      unique_id: "",
      groupName: g.name,
      // Which side of the balance sheet this group sits on. Without it a group
      // selection had no way to tell an asset from income, so every credit
      // balance printed as a bare negative: Interest Income of 2,000 read as
      // "−2,000.00" in red on the very report showing income was earned.
      groupType: g.groupType,
    })),
    // `sub` carries the group name so the operator sees "Rameswaram Cash — Cash In
    // Hand" rather than the literal word "Account" against every row.
    ...ledgerGroups.map(l => ({
      id: l.id,
      label: l.name,
      sub: l.groupName || "Account",
      type: "ledger",
      unique_id: "",
      accountBranchId: l.branchId,
      kind: l.kind,
    })),
    ...members.map(m => ({ id: m.id, label: m.name || `Member #${m.id}`, sub: m.customerUniqueId || "", type: "member", unique_id: m.customerUniqueId || "" })),
    ...staff.map(s => ({ id: s.id, label: s.name || `Staff #${s.id}`, sub: s.designation || "", type: "staff", unique_id: String(s.staffId || s.id) })),
  ].filter(e =>
    e.label.toLowerCase().includes(ledgerQuery.toLowerCase()) ||
    e.sub.toLowerCase().includes(ledgerQuery.toLowerCase())
  );

  // Closing balance for ONE account, by the same rules the per-account report uses.
  //
  // Classified by GROUP, never by name: "Loans Outstanding - FEDERAL BANK" contains
  // "bank" while being an asset, not a bank account.
  const accountClosing = async (acct) => {
    const accountBranch = acct.branchId ?? branchId;

    // Loans Outstanding is derived from `loanstatement`, not from ledger postings:
    // an EMI row in `transactions` is a single undivided amount, so it cannot say
    // how much reduced the debt versus paid interest.
    if (acct.kind === "loans") {
      if (!accountBranch) {
        return { opening: 0, debit: 0, credit: null, movement: 0, closing: 0 };
      }
      const res = await axios.get("/api/ledger/loans-outstanding", {
        baseURL: API_BASE_URL,
        params: { branchId: accountBranch, accountName: acct.name },
      });
      const d = res.data?.data || {};
      const outstanding = Number(d.outstanding) || 0;
      // The disbursement IS the opening movement here, so opening stays zero —
      // matching the per-account report's own rule.
      //
      // credit is null, not 0: this figure comes from `loanstatement`, which
      // records a net outstanding rather than the two sides, so there is no
      // credit total to report. A zero would claim there were no repayments.
      return {
        opening: 0,
        debit: outstanding,
        credit: null,
        movement: outstanding,
        closing: outstanding,
      };
    }

    // Reuses the day-end's opening balance, which already sums ledger entries
    // before the date AND adds movement not yet posted to the ledger.
    const opening = accountBranch
      ? Number(
        (await axios.get("/api/ledger/opening-balance", {
          baseURL: API_BASE_URL,
          params: { branchId: accountBranch, date: fromDate, accountId: acct.id },
        })).data?.data?.balance
      ) || 0
      : 0;

    // Movement comes from the LEDGER, the same source the per-account report
    // reads. It used to be derived from `transactions` filtered by cash_type —
    // 'online' for a bank account, 'cash' for cash — which quietly disagreed with
    // the account view in two ways:
    //
    //   - a ledger posting with no matching transactions row was invisible
    //   - a disbursement carries cash_type NULL and branchId NULL, so it matched
    //     neither filter. Bank Accounts showed 0.00 while the account report,
    //     reading the ledger, correctly showed -95,640.
    //
    // One source removes the class of disagreement rather than this instance of it.
    let movement = 0;
    // Filtered by the SERVER. This used to fetch every row the account had ever
    // had and narrow the range in the browser, which is fine at 14 rows and not
    // at a year's worth. getModelData handles fromDate/toDate generically for any
    // model carrying a `date` column, so no new endpoint was needed — and
    // ledger_transaction now has an (accountId, date) index behind it.
    const ltRes = await axios.get("/api/getModelData/ledger_transaction", {
      baseURL: API_BASE_URL,
      params: { filter: JSON.stringify({ accountId: acct.id }), fromDate, toDate },
    });
    const entries = Array.isArray(ltRes.data?.data) ? ltRes.data.data
      : Array.isArray(ltRes.data) ? ltRes.data : [];

    // receipt = debit, payment = credit. Amounts are stored in paise.
    //
    // Accumulated SEPARATELY rather than netted. Loan Proceeds Payable takes a
    // lakh in and a lakh out on a single disbursement: netted, that reads 0.00
    // and the column claims nothing happened to the account the whole loan
    // passed through.
    let debit = 0;
    let credit = 0;
    const isSeed = (e) =>
      String(e.date || "").split("T")[0] === fromDate &&
      /^opening balance/i.test(e.particulars || "");

    for (const e of entries) {
      // An "Opening Balance" seed dated ON fromDate is already inside `opening`;
      // counting it again would double it. Same rule as the per-account view.
      if (isSeed(e)) continue;
      const amt = (Number(e.amount) || 0) / 100;
      if (String(e.transaction_type).toLowerCase() === "receipt") debit += amt;
      else credit += amt;
    }

    movement = debit - credit;

    return { opening, debit, credit, movement, closing: opening + movement };
  };

  // One summary row per account in the group, grouped by branch, plus a group total.
  const buildGroupReport = async () => {
    const accounts = ledgerGroups.filter(
      a => Number(a.ledgerId) === Number(selectedLedger.id)
    );

    // In PARALLEL: 32 serial round trips would visibly stall the page.
    const figures = await Promise.all(
      accounts.map(async (acct) => ({ acct, ...(await accountClosing(acct)) }))
    );

    // Group by branch so a superadmin reading four branches sees a subtotal for
    // each rather than 32 undifferentiated rows.
    const branches = new Map();
    for (const f of figures) {
      const key = f.acct.branchId ?? 0;
      if (!branches.has(key)) branches.set(key, []);
      branches.get(key).push(f);
    }

    const branchBlocks = [...branches.entries()]
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([bId, rows]) => ({
        branchId: bId,
        // Strip the branch prefix — it is already the block heading.
        rows: rows.map(r => ({
          id: r.acct.id,
          label: String(r.acct.name).replace(`${selectedLedger.groupName}`, "").trim() || r.acct.name,
          fullName: r.acct.name,
          kind: r.acct.kind,
          branchId: r.acct.branchId,
          opening: r.opening,
          debit: r.debit,
          credit: r.credit,
          movement: r.movement,
          closing: r.closing,
        })),
        subtotal: rows.reduce((s, r) => s + r.closing, 0),
        // Column subtotals, so a branch block adds up on its own rather than
        // leaving the reader to sum the rows by eye.
        subtotalDebit: rows.reduce((s, r) => s + (Number(r.debit) || 0), 0),
        subtotalCredit: rows.reduce((s, r) => s + (Number(r.credit) || 0), 0),
      }));

    setReport({
      ledger_name: selectedLedger.label,
      ledger_type: "group",
      from_date: fromDate,
      to_date: toDate,
      branch_blocks: branchBlocks,
      group_total: figures.reduce((s, f) => s + f.closing, 0),
      // No longer set for groups: movement is read from the ledger for every
      // account now, so a Savings or Borrowings group shows its real postings
      // rather than a column of zeros needing an explanation.
      no_txn_channel: false,
      derived_from_loanstatement: accounts.some(a => a.kind === "loans"),
      rows: [],
    });
  };

  const handleCreateReport = async () => {
    if (!selectedLedger) { alert("Please select a ledger / member / account."); return; }
    setLoading(true);
    setReport(null);
    setLoanInfo(null);
    setLoanStatements([]);
    try {
      const branchParam = (!isSuperAdmin && branchId) ? { branchId } : {};

      // An ACCOUNT GROUP reports as one summary row per account plus a group total,
      // whatever its size. Groups run from 4 accounts (Cash In Hand) to 32 (Loans
      // Outstanding); a size-dependent layout would make the page behave differently
      // with no visible reason, and full transaction lines are not viable either —
      // Rameswaram Cash alone is 7,067 rows for three weeks.
      //
      // Transaction detail stays one click away: each row opens that account's own
      // report, which already works.
      if (selectedLedger.type === "group") {
        await buildGroupReport();
        setLoading(false);
        return;
      }

      // A ledger account is reported from the ledger plus the transactions that have
      // not been posted to it yet. Reading receipt_voucher / payment_voucher — 1 row
      // and 0 rows respectively — rendered an empty report for every account.
      let ledgerEntries = [];
      let channelTxns = [];
      let ledgerOpening = 0;
      let loanSummary = null;

      if (selectedLedger.type === "ledger") {
        // Branch comes from the ACCOUNT, not the logged-in user: filtering by the
        // viewer's branch made another branch's Bank account display branch 1's
        // transactions during testing.
        const accountBranch = selectedLedger.accountBranchId ?? branchId;

        const [ltRes, obRes] = await Promise.all([
          axios.get("/api/getModelData/ledger_transaction", {
            baseURL: API_BASE_URL,
            // Server-side range, as above — see the note on the statement fetch.
            params: {
              filter: JSON.stringify({ accountId: selectedLedger.id }),
              fromDate,
              toDate,
            },
          }),
          // Reuses the day-end's own opening balance, which already sums ledger
          // entries before the date AND adds movement not yet posted. Re-deriving it
          // here would drift from the figure the day-end shows.
          accountBranch
            ? axios.get("/api/ledger/opening-balance", {
              baseURL: API_BASE_URL,
              params: { branchId: accountBranch, date: fromDate, accountId: selectedLedger.id },
            })
            : Promise.resolve({ data: { data: { balance: 0 } } }),
        ]);

        const toArrE = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
        ledgerEntries = toArrE(ltRes);
        ledgerOpening = Number(obRes.data?.data?.balance) || 0;

        // Transactions already posted to THIS account. A posting writes
        // sourceModel/sourceId back to the row it came from, so the link is
        // exact and the same money is never listed twice.
        //
        // Without this the statement showed every posted deposit as BOTH a
        // LEDGER row and a TRANSACTION row and added both into the running
        // balance: DINDIGUL Cash listed LT-1/TX-1 (10,000) and LT-3/TX-2 (1,000)
        // and closed at 22,000 against an account holding 11,000.
        const postedTxnIds = new Set(
          ledgerEntries
            .filter((e) => e.sourceModel === "transactions" && e.sourceId != null)
            .map((e) => Number(e.sourceId))
        );

        // Only Cash and Bank map to a transaction channel. Every other group —
        // Savings, Loans Outstanding, Borrowings, Income — has no marker on a
        // transaction row saying which ledger account it belongs to, so those
        // accounts show ledger entries alone until postings are built.
        // Loans Outstanding is derived from `loanstatement`, not from a
        // transaction channel: an EMI row in `transactions` is a single undivided
        // amount, so it cannot say how much reduced the debt versus paid interest.
        if (selectedLedger.kind === "loans" && accountBranch) {
          const res = await axios.get("/api/ledger/loans-outstanding", {
            baseURL: API_BASE_URL,
            params: { branchId: accountBranch, accountName: selectedLedger.label },
          });
          const d = res.data?.data || {};
          loanSummary = {
            disbursed: Number(d.disbursed) || 0,
            collected: Number(d.collected) || 0,
            outstanding: Number(d.outstanding) || 0,
            loans: Number(d.loans) || 0,
          };
        } else if (selectedLedger.kind && accountBranch) {
          const res = await axios.get("/api/getModelData/transactions", {
            baseURL: API_BASE_URL,
            params: {
              filter: JSON.stringify({
                branchId: accountBranch,
                cash_type: selectedLedger.kind === "bank" ? "online" : "cash",
              }),
              fromDate, toDate,
            },
          });
          // Only what is NOT yet in the ledger — the rule stated at the top of
          // this block but never applied. Whatever survives is genuinely
          // unposted, so the TRANSACTION badge on it is accurate.
          //
          // The LEDGER row is the one kept: it carries the double entry, it is
          // what the Balance Sheet, Day-End and opening-balance endpoint read,
          // and it also holds entries with no transaction at all (opening
          // balances, capital receipts, loan postings) which preferring the
          // transaction side would drop.
          // Nothing dated before this account's ledger begins. Days before
          // go-live are already summed into the "Opening Balance" entry posted at
          // setup; listing their transactions as well counted them twice —
          // Uchipuli Cash for 01–08 Oct read -1,35,223 against a ledger (and
          // day-end) of 93,765, because 1–5 Oct deposits and withdrawals were
          // added on top of the opening balance that already contained them.
          const ledgerStart = ledgerEntries
            .map((e) => String(e.date || "").split("T")[0])
            .filter(Boolean)
            .sort()[0] || null;
          channelTxns = toArrE(res).filter(t => {
            if (postedTxnIds.has(Number(t.id))) return false;
            if (isSystemWithdrawal(t)) return false;
            const d = String(t.date || "").split("T")[0];
            if (ledgerStart && d < ledgerStart) return false;
            return d >= fromDate && d <= toDate;
          });
        }
      }

      // Members and staff still report from vouchers — those paths read tables that
      // hold real data and are unaffected by this change.
      const [rvRes, pvRes] = selectedLedger.type === "ledger"
        ? [{ data: { data: [] } }, { data: { data: [] } }]
        : await Promise.all([
          axios.get("/api/getModelData/receipt_voucher", {
            baseURL: API_BASE_URL,
            params: {
              filter: JSON.stringify({
                ledger_id: selectedLedger.id,
                ledger_type: selectedLedger.type
              }),
              fromDate, toDate,
              ...branchParam,
            },
          }),
          axios.get("/api/getModelData/payment_voucher", {
            baseURL: API_BASE_URL,
            params: {
              filter: JSON.stringify({
                ledger_id: selectedLedger.id,
                ledger_type: selectedLedger.type
              }),
              fromDate, toDate,
              ...branchParam,
            },
          }),
        ]);

      // If member type, also fetch loan details and loan statement
      let _loanInfoData = null;
      let _loanStmts = [];
      if (selectedLedger.type === "member") {
        try {
          const [mdRes, lsRes] = await Promise.all([
            axios.get("/api/getModelData/member_details", {
              baseURL: API_BASE_URL,
              params: { filter: JSON.stringify({ customerId: selectedLedger.id }), includeDepth: 1 },
            }),
            axios.get("/api/getModelData/loanstatement", {
              baseURL: API_BASE_URL,
              params: { filter: JSON.stringify({}) },
            }),
          ]);
          const toArrL = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
          const mdArr = toArrL(mdRes);
          const lsArr = toArrL(lsRes);
          if (mdArr.length > 0) {
            const md = mdArr[0];
            _loanInfoData = {
              amountToRelease: md.amountToRelease,
              loanType: md.loanType,
              loanStatus: md.loanStatus,
              loanId: md.loanId,
              loanCloseDate: md.loanCloseDate,
              createdAt: md.createdAt,
            };
            setLoanInfo(_loanInfoData);
            _loanStmts = lsArr.filter(ls => ls.memberId === md.id).sort((a, b) => a.dueNo - b.dueNo);
            setLoanStatements(_loanStmts);
          }
        } catch (e) {
          console.error("Error fetching loan info:", e);
        }
      }

      const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
      const receipts = toArr(rvRes);
      const payments = toArr(pvRes);

      // Build combined rows: payments = Debit, receipts = Credit
      // For members, also inject loan disbursement & EMI rows from loanstatement
      const loanRows = [];
      if (_loanInfoData && Number(_loanInfoData.amountToRelease || 0) > 0) {
        // Loan Disbursement — money paid OUT to member → Debit
        loanRows.push({
          date: _loanInfoData.createdAt ? String(_loanInfoData.createdAt).split("T")[0] : fromDate,
          particulars: `Loan Disbursement (${_loanInfoData.loanType || "Loan"})`,
          type: "loan_disbursement",
          invoice_no: _loanInfoData.loanId || "—",
          // PAISE -> RUPEES. This is a DEBIT in a running balance, not a label:
          // the rows around it (receipts, payments, EMI) are rupees, so an
          // unconverted figure does not merely display wrong, it throws the
          // member's whole ledger balance out by 100x the loan.
          debit: toRupees(_loanInfoData.amountToRelease),
          credit: 0,
          outstanding: 0,
          status: null,
        });
      }
      if (_loanStmts.length > 0) {
        // Summarise all EMIs into 2 rows: one for received, one for pending
        const receivedStmts = _loanStmts.filter(ls => ls.received === "Received");
        const pendingStmts  = _loanStmts.filter(ls => ls.received !== "Received");

        const totalReceived = receivedStmts.reduce((s, ls) => s + Number(ls.emiReceived || ls.total || 0), 0);
        // For pending: use total first, then arrearTotal if total=0, then outstanding as last resort
        const totalPending  = pendingStmts.reduce((s, ls) => {
          const amt = Number(ls.total || 0) + Number(ls.arrearTotal || 0);
          return s + (amt > 0 ? amt : Number(ls.outstanding || 0));
        }, 0);

        // Last payment date for the received summary row
        const lastPayDate = receivedStmts.length > 0
          ? receivedStmts.reduce((latest, ls) => {
              const d = ls.paymentDate ? String(ls.paymentDate).split("T")[0] : "";
              return d > latest ? d : latest;
            }, "")
          : fromDate;

        if (totalReceived > 0) {
          loanRows.push({
            date: lastPayDate || fromDate,
            particulars: `EMI Repayments Received (${receivedStmts.length} instalment${receivedStmts.length !== 1 ? "s" : ""})`,
            type: "emi_received",
            invoice_no: `EMI×${receivedStmts.length}`,
            debit: 0,
            credit: totalReceived,
            outstanding: 0,
            status: "Received",
          });
        }

        if (totalPending > 0) {
          loanRows.push({
            date: fromDate,
            particulars: `EMI Pending / Liability (${pendingStmts.length} instalment${pendingStmts.length !== 1 ? "s" : ""})`,
            type: "emi_pending",
            invoice_no: `EMI×${pendingStmts.length}`,
            debit: 0,
            credit: 0,
            outstanding: totalPending,
            status: "Pending",
          });
        }
      }

      const rows = [
        ...payments.map(p => ({
          date: p.date,
          particulars: `By ${p.mop || "CASH"}${p.narration ? " – " + p.narration : ""}`,
          type: "payment",
          invoice_no: `PV-${p.payment_no}`,
          debit: Number(p.amount || 0),
          credit: 0,
          outstanding: Number(p.outstanding_amount || 0),
          status: null,
        })),
        ...receipts.map(r => ({
          date: r.date,
          particulars: `By ${r.mor || "CASH"}${r.narration ? " – " + r.narration : ""}`,
          type: "receipt",
          invoice_no: `RV-${r.receipt_no}`,
          debit: 0,
          credit: Number(r.amount || 0),
          outstanding: Number(r.loan_outstanding || 0),
          status: null,
        })),
        // Ledger entries. Amounts are stored in PAISE, so /100 — mixing this up is a
        // 100x error. `receipt` increases the account, `payment` decreases it.
        ...ledgerEntries.map(e => ({
          date: String(e.date || "").split("T")[0],
          particulars: e.particulars || "Ledger entry",
          type: "ledger",
          invoice_no: `LT-${e.id}`,
          debit: String(e.transaction_type).toLowerCase() === "receipt" ? Number(e.amount || 0) / 100 : 0,
          credit: String(e.transaction_type).toLowerCase() === "payment" ? Number(e.amount || 0) / 100 : 0,
          outstanding: 0,
          status: null,
        })),
        // Transactions for this account's channel. Amounts are in RUPEES here — no
        // division, unlike the ledger rows above.
        ...channelTxns.map(t => ({
          date: String(t.date || "").split("T")[0],
          particulars: t.narration || `${t.transactionType} — ${t.customerUniqueId || ""}`.trim(),
          type: "transaction",
          invoice_no: `TX-${t.id}`,
          debit: String(t.transactionType).toLowerCase() === "deposit" ? Number(t.amount || 0) : 0,
          credit: String(t.transactionType).toLowerCase() === "withdraw" ? Number(t.amount || 0) : 0,
          outstanding: 0,
          status: null,
        })),
        // Loans Outstanding: two movement lines rather than one per EMI. Branch 1
        // has 42,834 received EMIs — listing each would be unreadable, and the
        // ledger question is "how much is still out", not "which instalment".
        ...(loanSummary && loanSummary.disbursed > 0 ? [{
          date: fromDate,
          particulars: `Loan disbursements (${loanSummary.loans} loan${loanSummary.loans !== 1 ? "s" : ""})`,
          type: "loan_disbursement",
          invoice_no: "PRINCIPAL",
          debit: loanSummary.disbursed,
          credit: 0,
          outstanding: 0,
          status: null,
        }] : []),
        ...(loanSummary && loanSummary.collected > 0 ? [{
          date: toDate,
          particulars: "EMI principal recovered",
          type: "emi_received",
          invoice_no: "PRINCIPAL",
          debit: 0,
          credit: loanSummary.collected,
          outstanding: 0,
          status: "Received",
        }] : []),
        ...loanRows,
      ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

      // For a ledger account the opening balance is the ledger's own position as at
      // fromDate. The old rule — take the first row's `outstanding` — described a
      // member's loan balance and means nothing for a cash or bank account.
      //
      // getOpeningBalance counts entries strictly BEFORE fromDate, plus any
      // opening-balance entry dated exactly ON it. Only that second kind is already
      // inside the figure, so only it is subtracted before the rows add it back.
      //
      // Subtracting every in-range entry drove Bank to minus Rs50,00,000: its seed is
      // dated 20 Aug, so it was never in the 19 Aug opening, yet was deducted anyway.
      let openingBal = 0;
      if (selectedLedger.kind === "loans") {
        // Starts at zero: the disbursement line IS the opening movement, and
        // adding a ledger opening on top would double the balance.
        openingBal = 0;
      } else if (selectedLedger.type === "ledger") {
        // Only an "Opening Balance" SEED dated on fromDate is inside the figure
        // getOpeningBalance returned — it matches that endpoint's own condition:
        //     date < fromDate  OR  (date = fromDate AND particulars LIKE 'Opening Balance%')
        //
        // Subtracting every row dated fromDate instead invented an opening out of
        // the day's own activity. A single Rs.95,640 withdrawal on 31 Aug gave
        // countedInOpening = -95,640, so openingBal became +95,640 and the closing
        // landed on zero — the report showed the bank flat on the day it went
        // Rs.95,640 overdrawn, which is the one thing it existed to reveal.
        const countedInOpening = rows
          .filter(
            r =>
              r.type === "ledger" &&
              r.date === fromDate &&
              /^opening balance/i.test(r.particulars || "")
          )
          .reduce((sum, r) => sum + r.debit - r.credit, 0);
        openingBal = ledgerOpening - countedInOpening;
      } else if (rows.length > 0) {
        openingBal = rows[0].outstanding;
      }

      // Calculate running balance
      let balance = openingBal;
      const rowsWithBalance = rows.map(row => {
        balance = balance + row.debit - row.credit;
        return { ...row, balance };
      });

      setReport({
        ledger_name: selectedLedger.label,
        ledger_type: selectedLedger.type,
        member_id: selectedLedger.unique_id,
        from_date: fromDate,
        to_date: toDate,
        opening_balance: openingBal,
        rows: rowsWithBalance,
        closing_balance: balance,
        // Set for ledger accounts outside Cash In Hand and Bank Accounts. A
        // transaction row records its channel (cash/online) but not which ledger
        // account it belongs to, so Savings, Loans Outstanding, Borrowings and the
        // income accounts can only show ledger entries until postings exist. Saying
        // so beats an unexplained short report.
        no_txn_channel: selectedLedger.type === "ledger" && !selectedLedger.kind,
        loan_summary: loanSummary,
      });
    } catch (err) {
      console.error("Error generating report:", err);
      alert("Error generating report. Please try again.");
    }
    setLoading(false);
  };

  const selectLedger = entry => {
    setSelectedLedger(entry);
    setShowSearch(false);
    setLedgerQuery("");
  };

  // Selecting an account row inside a group report opens that account's own full
  // ledger. This is what makes the summary-only group view acceptable: transaction
  // detail is one click away rather than a re-search.
  const openAccountFromGroup = (row) => {
    setSelectedLedger({
      id: row.id,
      label: row.fullName,
      sub: selectedLedger?.groupName || "",
      type: "ledger",
      unique_id: "",
      accountBranchId: row.branchId,
      kind: row.kind,
    });
    setReport(null);
  };

  const money = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const branchLabel = (bId) => {
    if (!bId) return "Shared / no branch";
    const b = branches.find(x => Number(x.id) === Number(bId));
    return b ? `Branch ${bId} · ${b.branchName}` : `Branch ${bId}`;
  };

  const totalDebit = report?.rows?.reduce((s, r) => s + r.debit, 0) || 0;
  const totalCredit = report?.rows?.reduce((s, r) => s + r.credit, 0) || 0;

  /**
   * Is the selected account an ASSET — Cash, Bank, or Loans Outstanding?
   *
   * `kind` is set only for those three groups when the account list is built
   * (see the groupName switch above), so it doubles as the asset test without a
   * second list of group names to keep in step.
   */
  // A GROUP has no `kind` — that is set per account — so it is classified by its
  // own group_type instead. 'Asset' is the literal stored in base_ledger_group,
  // alongside Liability, Equity, Income and Expense.
  //
  // An unknown or empty type falls through to asset-side deliberately: the legacy
  // groups filtered out above (BankAccount, DirectIncome, DirectExpenses) would
  // otherwise gain a Dr/Cr suffix derived from a value nothing here understands.
  // Keeping today's plain number is the safer reading.
  const isAssetLedger =
    selectedLedger?.type === "group"
      ? String(selectedLedger?.groupType || "") !== "Liability" &&
        String(selectedLedger?.groupType || "") !== "Equity" &&
        String(selectedLedger?.groupType || "") !== "Income"
      : selectedLedger?.type === "ledger" &&
        ["cash", "bank", "loans"].includes(selectedLedger?.kind);

  /**
   * An asset shows a plain signed balance; everything else keeps Dr/Cr.
   *
   * Dr/Cr is correct double-entry notation — on an asset a credit balance IS
   * negative — but it strips the minus and prints "95,640.00 Cr" in green, which
   * reads as money in the bank on the very report meant to reveal that the bank
   * is Rs.95,640 OVERDRAWN. This screen answers "what is in Rameshwarm Bank?",
   * and the answer is minus ninety-five thousand.
   *
   * Liabilities and income keep the suffix: a credit balance there is the normal
   * resting state (Unapplied Collections holding Rs.10,000 is not a problem), and
   * a bare minus sign would read as an error.
   */
  const balanceLabel = (bal) => {
    if (isAssetLedger) {
      if (bal === 0) return "0.00";
      return `${bal < 0 ? "−" : ""}${Math.abs(bal).toFixed(2)}`;
    }
    if (bal > 0) return `${Math.abs(bal).toFixed(2)} Dr`;
    if (bal < 0) return `${Math.abs(bal).toFixed(2)} Cr`;
    return "0.00";
  };

  const handlePrint = () => window.print();

  const toolbar = (
    <div className="flex flex-row flex-wrap items-end gap-3 w-full bg-white py-2.5 px-4 rounded-xl border border-border-light shadow-sm print:hidden">
      {/* From Date */}
      <div className="flex flex-col gap-1 min-w-[130px] flex-1">
        <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">From Date</label>
        <DatePicker
          selected={fromDate}
          onChange={dateStr => setFromDate(dateStr)}
          placeholder="YYYY-MM-DD"
          className="h-[32px] py-1 text-xs"
        />
      </div>

      {/* To Date */}
      <div className="flex flex-col gap-1 min-w-[130px] flex-1">
        <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">To Date</label>
        <DatePicker
          selected={toDate}
          onChange={dateStr => setToDate(dateStr)}
          placeholder="YYYY-MM-DD"
          className="h-[32px] py-1 text-xs"
        />
      </div>

      {/* Ledger Selection Input */}
      <div className="flex-1 min-w-[260px] flex flex-col gap-1">
        <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">Ledger / Member / Account</label>
        <div className="flex relative">
          <input
            className={`w-full bg-white rounded-l-lg border border-r-0 border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[32px] text-xs px-3 focus:outline-none transition-all cursor-pointer ${
              selectedLedger ? "bg-emerald-50/20 border-emerald-300" : ""
            }`}
            value={selectedLedger ? selectedLedger.label : ""}
            readOnly
            placeholder="Click to select ledger, member or account..."
            onClick={() => setShowSearch(true)}
          />
          <button
            type="button"
            className="bg-primary hover:bg-primary-hover text-white rounded-r-lg px-3 h-[32px] flex items-center justify-center transition-colors text-xs"
            onClick={() => setShowSearch(true)}
          >
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
          {selectedLedger && (
            <button
              type="button"
              onClick={() => { setSelectedLedger(null); setReport(null); }}
              className="absolute right-12 top-1 text-rose-500 hover:text-rose-700 p-0.5 rounded-full hover:bg-rose-50 transition-colors"
            >
              <i className="fa-solid fa-circle-xmark text-sm"></i>
            </button>
          )}
        </div>
        {selectedLedger?.unique_id && (
          <span className="text-[10px] text-ink-400 mt-0.5">
            ID: <strong className="text-primary font-mono">{selectedLedger.unique_id}</strong>
          </span>
        )}
      </div>

      {/* Action Button */}
      <div className="flex flex-row items-end gap-2">
        <Button
          onClick={handleCreateReport}
          disabled={loading || !selectedLedger}
          variant={selectedLedger ? "primary" : "secondary"}
          size="sm"
          className="h-[32px] font-bold text-xs"
        >
          {loading && <LogoLoader size="xs" className="inline-flex mr-1.5 p-0" />}
          Create Report
        </Button>
        {report && (
          <Button onClick={handlePrint} variant="secondary" size="sm" className="h-[32px] font-bold text-xs print:hidden">
            <i className="fa-solid fa-print mr-1.5"></i>
            Print Ledger
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      toolbar={toolbar}
      className="gap-0 pt-0"
    >
      {loading && <Loader message="Generating ledger report..." />}

      {/* Not overflow-auto below: PageLayout fixes the page height, and an outer
          scroll region here fights the inner tables' own `flex-1 min-h-0` — they
          shrink toward zero while this element believes its content fits. The
          tables scroll individually. */}
      {report && (
        <div id="ledger-report-content" className="flex flex-col gap-2.5 min-h-0 flex-1 print:overflow-visible">
          {/* Print only header */}
          <div className="hidden print:flex flex-col items-center text-center mb-6">
            <img src={Logo} alt="Jothilinga Logo" className="w-28 h-auto object-contain mb-2" />
            <h1 className="text-xl font-black text-ink-950 uppercase tracking-wide">Jothilinga Financial Solutions Pvt. Ltd.</h1>
            <p className="text-xs text-ink-500 font-medium">Ledger Report Statement</p>
          </div>

          <div className="flex items-center justify-between border-b border-border-light pb-2">
            <div className="flex flex-col">
              <h3 className="text-lg font-black text-ink-900 flex items-center gap-2">
                <i className="fa-solid fa-book-open text-primary"></i>
                {report.ledger_name} Ledger Statement
              </h3>
              <p className="text-xs text-ink-500 font-medium">
                Period: <span className="font-semibold text-ink-700">{fmt(report.from_date)}</span> to <span className="font-semibold text-ink-700">{fmt(report.to_date)}</span>
              </p>
            </div>
            {report.member_id && (
              <span className="bg-primary/5 text-primary border border-primary/20 rounded-xl px-3 py-1 text-xs font-bold font-mono">
                ID: {report.member_id}
              </span>
            )}
          </div>

          {/* Account Group summary — one row per account, subtotal per branch,
              then the group total. Clicking a row opens that account's own
              full ledger, which is what makes a summary-only view acceptable. */}
          {report.ledger_type === "group" && (
            <div className="flex flex-col min-h-0 flex-1">
              {/* The table scrolls INSIDE its own box, so the group total below it
                  stays on screen. Without a height cap the table grows to fit all
                  32 Loans Outstanding rows and pushes the total past the fold —
                  PageLayout fixes the viewport height and hides overflow, so the
                  page itself cannot scroll to reach it. */}
              <Table className="min-h-[380px] max-h-[calc(100vh-360px)] print:max-h-none print:overflow-visible">
                <Table.Head>
                  <tr>
                    {/* Debit / Credit rather than a single netted Movement, and
                        the same wording as the per-account report a click away. */}
                    <Table.HeadCell align="left">Account</Table.HeadCell>
                    <Table.HeadCell align="right" className="w-36">Opening (₹)</Table.HeadCell>
                    <Table.HeadCell align="right" className="w-36">Debit (₹)</Table.HeadCell>
                    <Table.HeadCell align="right" className="w-36">Credit (₹)</Table.HeadCell>
                    <Table.HeadCell align="right" className="w-40">Closing (₹)</Table.HeadCell>
                  </tr>
                </Table.Head>
                <Table.Body>
                  {report.branch_blocks.length === 0 ? (
                    <Table.EmptyRow colSpan={5} message="No accounts in this group." />
                  ) : (
                    report.branch_blocks.map(block => (
                      <React.Fragment key={`branch_${block.branchId}`}>
                        <Table.Row className="bg-primary/5 font-bold border-b border-border-light">
                          <Table.Cell align="left" className="text-ink-900">
                            {branchLabel(block.branchId)}
                          </Table.Cell>
                          <Table.Cell align="right">—</Table.Cell>
                          <Table.Cell align="right" className="text-rose-600">
                            {money(block.subtotalDebit)}
                          </Table.Cell>
                          <Table.Cell align="right" className="text-emerald-600">
                            {money(block.subtotalCredit)}
                          </Table.Cell>
                          {/* Red only where a negative is genuinely bad news —
                              an overdrawn asset. On a liability or income
                              account a credit balance is the normal resting
                              state, and balanceLabel renders it as "Cr". */}
                          <Table.Cell
                            align="right"
                            className={
                              isAssetLedger && block.subtotal < 0
                                ? "text-rose-600"
                                : "text-ink-900"
                            }
                          >
                            {balanceLabel(block.subtotal)}
                          </Table.Cell>
                        </Table.Row>
                        {block.rows.map(r => (
                          <Table.Row
                            key={`acct_${r.id}`}
                            onClick={() => openAccountFromGroup(r)}
                            className="cursor-pointer hover:bg-primary/5 transition-colors"
                          >
                            <Table.Cell align="left" className="text-ink-700 pl-8">
                              {r.label}
                            </Table.Cell>
                            <Table.Cell align="right" className="text-ink-500">
                              {r.kind === "loans" ? "—" : balanceLabel(r.opening)}
                            </Table.Cell>
                            {/* Read from the ledger for EVERY account, not just Cash
                                and Bank, so Savings, Borrowings and the income
                                accounts no longer show a dash where they have real
                                postings.
                                Loans Outstanding carries credit === null — its
                                figure comes from `loanstatement`, which records a net
                                outstanding rather than two sides, so a 0.00 there
                                would claim there had been no repayments. */}
                            <Table.Cell align="right" className="text-rose-600">
                              {money(r.debit)}
                            </Table.Cell>
                            <Table.Cell align="right" className="text-emerald-600">
                              {r.credit === null ? "—" : money(r.credit)}
                            </Table.Cell>
                            <Table.Cell
                              align="right"
                              className={`font-semibold ${
                                isAssetLedger && r.closing < 0
                                  ? "text-rose-600"
                                  : "text-ink-900"
                              }`}
                            >
                              {balanceLabel(r.closing)}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </Table.Body>
              </Table>

              {/* shrink-0 so the total is never squeezed out by the table above */}
              <div className="flex items-center justify-between border-t-2 border-ink-900 mt-2 pt-3 px-3 shrink-0 bg-white">
                <span className="text-sm font-black text-ink-900 uppercase tracking-wide">
                  Group Total — {report.ledger_name}
                </span>
                <span
                  className={`text-lg font-black ${
                    isAssetLedger && report.group_total < 0
                      ? "text-rose-600"
                      : "text-ink-900"
                  }`}
                >
                  ₹{balanceLabel(report.group_total)}
                </span>
              </div>

              {(report.no_txn_channel || report.derived_from_loanstatement) && (
                <p className="text-[11px] text-ink-400 mt-3 px-3 leading-relaxed shrink-0">
                  {report.derived_from_loanstatement
                    ? "Loans Outstanding is derived from the loan schedule, not from ledger postings: an EMI in transactions is a single undivided amount and cannot say how much reduced the debt versus paid interest."
                    : "This group has no transaction channel — a transaction records whether it was cash or online, but not which ledger account it belongs to. Movement appears once ledger postings are live."}
                </p>
              )}
            </div>
          )}

          {/* Ledger Table */}
          {report.ledger_type !== "group" && (
          <div className="flex flex-col min-h-0 flex-1">
            <Table className="min-h-[380px] max-h-[calc(100vh-360px)] print:max-h-none print:overflow-visible">
              <Table.Head>
                <tr>
                  <Table.HeadCell align="center" className="w-28">Date</Table.HeadCell>
                  <Table.HeadCell align="left">Particulars</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-28">Type</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-28">Voucher No</Table.HeadCell>
                  <Table.HeadCell align="right" className="w-36">Debit (₹)</Table.HeadCell>
                  <Table.HeadCell align="right" className="w-36">Credit (₹)</Table.HeadCell>
                  <Table.HeadCell align="right" className="w-40">Balance (₹)</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {/* Opening Balance Row */}
                <Table.Row className="bg-amber-50/10 font-semibold border-b border-border-light">
                  <Table.Cell align="center" className="text-ink-600">{fmt(report.from_date)}</Table.Cell>
                  <Table.Cell align="left" className="text-ink-900 font-bold">Opening Balance</Table.Cell>
                  <Table.Cell align="center">—</Table.Cell>
                  <Table.Cell align="center">—</Table.Cell>
                  <Table.Cell align="right" className="text-rose-600">
                    {report.opening_balance > 0 ? report.opening_balance.toFixed(2) : "—"}
                  </Table.Cell>
                  <Table.Cell align="right" className="text-emerald-600">
                    {report.opening_balance < 0 ? Math.abs(report.opening_balance).toFixed(2) : "—"}
                  </Table.Cell>
                  <Table.Cell align="right" className="text-ink-700 font-bold">
                    {balanceLabel(report.opening_balance)}
                  </Table.Cell>
                </Table.Row>

                {/* Transaction Rows */}
                {report.rows.length === 0 ? (
                  <Table.EmptyRow
                    colSpan={7}
                    message={
                      report.no_txn_channel
                        ? "No ledger entries for this period. Transaction-level detail for this account appears once ledger postings are live."
                        : "No entries found for this period."
                    }
                  />
                ) : (
                  report.rows.map((row, idx) => {
                    const typeMeta = {
                      receipt:          { bg: "bg-emerald-50 text-emerald-700 border-emerald-200",  label: "Receipt" },
                      payment:          { bg: "bg-rose-50 text-rose-700 border-rose-200",  label: "Payment" },
                      ledger:           { bg: "bg-indigo-50 text-indigo-700 border-indigo-200",  label: "Ledger" },
                      transaction:      { bg: "bg-sky-50 text-sky-700 border-sky-200",  label: "Transaction" },
                      loan_disbursement:{ bg: "bg-purple-50 text-purple-700 border-purple-200",  label: "Disbursement" },
                      emi_received:     { bg: "bg-blue-50 text-blue-700 border-blue-200",  label: "EMI (Paid)" },
                      emi_pending:      { bg: "bg-amber-50 text-amber-700 border-amber-200",  label: "EMI (Pending)" },
                    }[row.type] || { bg: "bg-ink-50 text-ink-700 border-ink-200", label: row.type };

                    const isPending = row.type === "emi_pending";

                    return (
                      <Table.Row key={idx} className={isPending ? "bg-amber-50/10" : ""}>
                        <Table.Cell align="center">{fmt(row.date)}</Table.Cell>
                        <Table.Cell align="left" className="font-semibold text-ink-800">
                          {row.particulars || "—"}
                          {isPending && (
                            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded ml-2 uppercase tracking-wide">
                              Expected
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell align="center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${typeMeta.bg}`}>
                            {typeMeta.label}
                          </span>
                        </Table.Cell>
                        <Table.Cell align="center" className="font-semibold text-primary font-mono text-xs">{row.invoice_no}</Table.Cell>
                        <Table.Cell align="right" className="font-semibold text-rose-600">
                          {row.debit > 0 ? row.debit.toFixed(2) : "—"}
                        </Table.Cell>
                        <Table.Cell align="right" className={`font-semibold ${isPending ? "text-amber-600 italic" : "text-emerald-600"}`}>
                          {isPending
                            ? `(${row.outstanding.toFixed(2)})`
                            : row.credit > 0 ? row.credit.toFixed(2) : "—"}
                        </Table.Cell>
                        <Table.Cell
                          align="right"
                          className={`font-bold ${
                            isAssetLedger && row.balance < 0 ? "text-rose-600" : "text-ink-900"
                          }`}
                        >
                          {balanceLabel(row.balance)}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table>
          </div>
          )}

          {/* Summary Cards — receipts/payments/closing describe a running account
              ledger. A group report has no single running balance, so its total
              is shown under its own table instead. */}
          {report.ledger_type !== "group" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
            <div className="bg-white p-5 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
              <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">Total Receipts (Cr)</span>
              <span className="text-lg font-black text-emerald-600">₹{totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
              <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">Total Payments (Dr)</span>
              <span className="text-lg font-black text-rose-600">₹{totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
              <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">Closing Balance</span>
              {/* Formatted here rather than through balanceLabel, so it has to
                  follow the same rule or the card and the table disagree. An
                  overdrawn asset is red: it is a shortfall, not a healthy credit
                  balance, and green on that figure is exactly what hid it. */}
              <span
                className={`text-lg font-black ${
                  isAssetLedger
                    ? report.closing_balance < 0
                      ? "text-rose-600"
                      : "text-emerald-600"
                    : report.closing_balance >= 0
                      ? "text-rose-600"
                      : "text-emerald-600"
                }`}
              >
                {isAssetLedger && report.closing_balance < 0 ? "−" : ""}₹
                {Math.abs(report.closing_balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                {isAssetLedger ? "" : ` ${report.closing_balance >= 0 ? "Dr" : "Cr"}`}
              </span>
            </div>
            {loanInfo && (
              <div className="bg-white p-5 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
                <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">Loan Disbursed</span>
                <span className="text-lg font-black text-primary">
                  ₹{Number(loanInfo.amountToRelease || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
          )}

          {/* Loan Info Bar (members only) */}
          {loanInfo && (
            <div className="bg-white p-6 rounded-2xl border border-border-light shadow-sm flex flex-wrap gap-6 items-center">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">Loan ID</span>
                <span className="text-sm font-bold text-ink-950">{loanInfo.loanId || "—"}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">Loan Type</span>
                <span className="text-sm font-bold text-ink-950">{loanInfo.loanType || "—"}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">Amount to Release</span>
                <span className="text-sm font-black text-rose-600">₹{Number(loanInfo.amountToRelease || 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">Loan Status</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                  loanInfo.loanStatus === "opened" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  loanInfo.loanStatus === "preclosed" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  loanInfo.loanStatus === "foreclosed" ? "bg-rose-50 text-rose-700 border-rose-200" :
                  "bg-ink-50 text-ink-700 border-ink-200"
                }`}>
                  {loanInfo.loanStatus || "—"}
                </span>
              </div>
              {loanInfo.loanCloseDate && (
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">Close Date</span>
                  <span className="text-sm font-bold text-ink-950">{fmt(loanInfo.loanCloseDate)}</span>
                </div>
              )}
            </div>
          )}

          {/* EMI Schedule (members only) */}
          {loanStatements.length > 0 && (
            <div className="flex flex-col gap-3 mt-4">
              <h4 className="text-sm font-black text-ink-800 flex items-center gap-2 uppercase tracking-wide">
                <i className="fa-solid fa-calendar-days text-primary"></i>
                EMI Repayment Schedule
                <span className="text-xs text-ink-400 font-normal normal-case">({loanStatements.length} instalments)</span>
              </h4>
              <div className="flex flex-col min-h-0">
                <Table>
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="center">#</Table.HeadCell>
                      <Table.HeadCell align="center">Due Date</Table.HeadCell>
                      <Table.HeadCell align="center">Payment Date</Table.HeadCell>
                      <Table.HeadCell align="right">Principal (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Interest (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Total EMI (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">EMI Received (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Arrear (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Outstanding (₹)</Table.HeadCell>
                      <Table.HeadCell align="center">Status</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {loanStatements.map((ls, idx) => {
                      const isPaid = ls.received === "Received";
                      const isPending = ls.received === "Pending";
                      const isOverdue = isPending && new Date(ls.dueDate) < new Date();
                      return (
                        <Table.Row key={ls.id || idx} className={isPaid ? "bg-emerald-50/5" : isOverdue ? "bg-rose-50/5" : ""}>
                          <Table.Cell align="center" className="text-ink-400">{ls.dueNo}</Table.Cell>
                          <Table.Cell align="center" className="font-semibold">{fmt(ls.dueDate)}</Table.Cell>
                          <Table.Cell align="center" className="text-ink-500">{ls.paymentDate ? fmt(ls.paymentDate) : "—"}</Table.Cell>
                          <Table.Cell align="right">{Number(ls.principal || 0).toFixed(2)}</Table.Cell>
                          <Table.Cell align="right">{Number(ls.interest || 0).toFixed(2)}</Table.Cell>
                          <Table.Cell align="right" className="font-bold text-primary">{Number(ls.total || 0).toFixed(2)}</Table.Cell>
                          <Table.Cell align="right" className="font-bold text-emerald-600">{Number(ls.emiReceived || 0).toFixed(2)}</Table.Cell>
                          <Table.Cell align="right" className={Number(ls.arrearTotal || 0) > 0 ? "text-rose-600 font-semibold" : "text-ink-400"}>
                            {Number(ls.arrearTotal || 0) > 0 ? Number(ls.arrearTotal).toFixed(2) : "—"}
                          </Table.Cell>
                          <Table.Cell align="right" className="font-bold text-rose-600">{Number(ls.outstanding || 0).toFixed(2)}</Table.Cell>
                          <Table.Cell align="center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                              isPaid ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              isPending ? "bg-amber-50 text-amber-700 border-amber-200" :
                              "bg-rose-50 text-rose-700 border-rose-200"
                            }`}>
                              {ls.received}
                            </span>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                  <tfoot>
                    <tr className="bg-canvas-muted border-t border-border-medium font-bold text-ink-800 text-sm">
                      <td colSpan={3} className="p-3 text-right">Total:</td>
                      <td className="p-3 text-right">₹{loanStatements.reduce((s,r)=>s+Number(r.principal||0),0).toFixed(2)}</td>
                      <td className="p-3 text-right">₹{loanStatements.reduce((s,r)=>s+Number(r.interest||0),0).toFixed(2)}</td>
                      <td className="p-3 text-right text-primary font-black">₹{loanStatements.reduce((s,r)=>s+Number(r.total||0),0).toFixed(2)}</td>
                      <td className="p-3 text-right text-emerald-600 font-black">₹{loanStatements.reduce((s,r)=>s+Number(r.emiReceived||0),0).toFixed(2)}</td>
                      <td className="p-3 text-right text-rose-600">₹{loanStatements.reduce((s,r)=>s+Number(r.arrearTotal||0),0).toFixed(2)}</td>
                      <td className="p-3 text-right text-rose-600">₹{loanStatements.reduce((s,r)=>s+Number(r.outstanding||0),0).toFixed(2)}</td>
                      <td className="p-3" />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!report && !loading && (
        <div className="bg-white rounded-2xl border border-border-light shadow-sm p-12 text-center flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center text-primary mb-4 text-2xl">
            <i className="fa-solid fa-book-open"></i>
          </div>
          <h4 className="text-base font-bold text-ink-800 mb-1">No Statement Generated</h4>
          <p className="text-xs text-ink-400 max-w-md">
            Select a ledger, member, or staff account from the dropdown above and click <strong className="text-primary">Create Report</strong> to generate the transaction ledger statement.
          </p>
        </div>
      )}

      {/* Ledger Search Popup */}
      {showSearch && (
        <div className="fixed inset-0 bg-ink-950/40 backdrop-blur-sm z-[1100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden border border-border-light">
            <div className="bg-gradient-to-r from-primary to-primary-hover px-6 py-4 text-white flex items-center justify-between">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass"></i>
                Select Ledger / Member / Account
              </h3>
              <button
                onClick={() => { setShowSearch(false); setLedgerQuery(""); }}
                className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg p-1 transition-colors"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            <div className="p-4 flex flex-col gap-4 flex-grow overflow-hidden">
              <input
                type="text"
                placeholder="Type name, ID or designation to search..."
                value={ledgerQuery}
                onChange={e => setLedgerQuery(e.target.value)}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-sm px-4 focus:outline-none transition-all"
                autoFocus
              />
              <div className="flex-grow overflow-y-auto min-h-0 border border-border-light rounded-xl">
                <table className="w-full text-xs text-ink-700 border-collapse">
                  <thead className="bg-canvas-muted text-[10px] font-bold uppercase tracking-wider text-ink-500 sticky top-0 border-b border-border-light">
                    <tr>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">ID / Sub</th>
                      <th className="p-3 text-center w-24">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {searchEntries.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-ink-400 py-6">
                          No results found matching your search.
                        </td>
                      </tr>
                    ) : (
                      searchEntries.map(e => (
                        <tr
                          key={`${e.type}_${e.id}`}
                          onClick={() => selectLedger(e)}
                          className="hover:bg-primary/5 cursor-pointer transition-colors"
                        >
                          <td className="p-3 font-semibold text-ink-900">{e.label}</td>
                          <td className="p-3 text-ink-500 font-mono">{e.sub || "—"}</td>
                          <td className="p-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
                              e.type === "member" ? "bg-amber-50 text-amber-700 border-amber-200" :
                              e.type === "staff" ? "bg-blue-50 text-blue-700 border-blue-200" :
                              e.type === "group" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              "bg-indigo-50 text-indigo-700 border-indigo-200"
                            }`}>
                              {e.type === "member" ? "Member" : e.type === "staff" ? "Staff" : e.type === "group" ? "Group" : "Account"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default LedgerReport;
